import { selectorPaths } from './selectorPaths'
import { Color } from 'src/api/misc/Color'
import { Instance } from 'mobx-state-tree'
import {
  enclosure as enclosureLayers,
  fastener as fastenerLayer,
  printedcircuitboardAssembly as printedcircuitboardAssemblyLayers,
  accessories as accessoriesLayers,
  contentEdge as contentEdgeLayers
} from 'src/constants/layers'
import * as typenames from './typenames'
import { front, top, right, bottom, left, back } from 'src/constants/sides'

const colorPicker = 'colorPicker'
const radio = 'radio'

interface Input {
  label?: string
  dataPath: string
  getValue?: (data: any) => any
}

interface InputWithOptions extends Input {
  getOptions?: (...args: any | any[]) => any
  options: {
    title: string
    disabled?: boolean
    value: string | number
  }[]
}

export interface ColorPicker extends InputWithOptions {
  type: 'colorPicker'
}

export interface RadioInput extends InputWithOptions {
  type: 'radio'
}

export interface StringInput extends Input {
  type: 'string'
}

export interface TextInput extends Input {
  type: 'string'
  placeholder?: string
}

export interface NumberInput extends Input {
  type: 'numberInput'
}

export type InputType =
  | ColorPicker
  | RadioInput
  | StringInput
  | TextInput
  | NumberInput

export const projectName: StringInput = {
  type: 'string',
  label: 'Name',
  dataPath: selectorPaths.project.name
}

export const projectDescription: TextInput = {
  type: 'string',
  label: 'Description',
  placeholder: 'Describe your new project...',
  dataPath: selectorPaths.project.description
}

export const coverColor: ColorPicker = {
  type: 'colorPicker',
  label: 'Cover Color',
  getValue: ({ name }) => name,
  getOptions: (
    options: ColorPicker['options'],
    instanceOptions: Instance<typeof Color>[]
  ) => {
    return options.map((option) => ({
      ...option,
      style: {
        backgroundColor: instanceOptions.find(
          ({ name }) => name === option.value
        )?.primary
      }
    }))
  },
  dataPath: selectorPaths.cover.color,
  options: [
    { title: 'Green', value: 'green' },
    { title: 'Blue', value: 'blue' },
    { title: 'Yellow', value: 'yellow' },
    { title: 'Red', value: 'red' },
    { title: 'Orange', value: 'orange' },
    { title: 'Black', value: 'black' },
    { title: 'White', value: 'white' },
    { title: 'Transparent', value: 'transparent' }
  ]
}

export const fastener: RadioInput = {
  type: 'radio',
  label: 'Fastener',
  dataPath: 'fasteners',
  options: [
    { title: 'Brackets', value: 'brackets' },
    { title: 'Distance Bolts', disabled: true, value: 'distanceBolts' }
  ]
}

export const printedcircuitboardWidth: NumberInput = {
  type: 'numberInput',
  label: 'Width',
  dataPath: selectorPaths.printedcircuitboard.width
}

export const printedcircuitboardLength: NumberInput = {
  type: 'numberInput',
  label: 'Length',
  dataPath: selectorPaths.printedcircuitboard.length
}

export const printedcircuitboardThickness: NumberInput = {
  type: 'numberInput',
  label: 'Thickness',
  dataPath: selectorPaths.printedcircuitboard.thickness
}

export const printedcircuitboardComponentSideHeight: NumberInput = {
  type: 'numberInput',
  label: 'Component side',
  dataPath: selectorPaths.printedcircuitboard.componentSide.height
}

export const printedcircuitboardSolderSideHeight: NumberInput = {
  type: 'numberInput',
  label: 'Solder side',
  dataPath: selectorPaths.printedcircuitboard.solderSide.height
}

export const contentGapFront: NumberInput = {
  type: 'numberInput',
  label: 'Front Gap',
  dataPath: selectorPaths.contentGaps.front
}

export const contentGapTop: NumberInput = {
  type: 'numberInput',
  label: 'Top Gap',
  dataPath: selectorPaths.contentGaps.top
}

export const contentGapRight: NumberInput = {
  type: 'numberInput',
  label: 'Right Gap',
  dataPath: selectorPaths.contentGaps.right
}

export const contentGapBottom: NumberInput = {
  type: 'numberInput',
  label: 'Bottom Gap',
  dataPath: selectorPaths.contentGaps.bottom
}

export const contentGapLeft: NumberInput = {
  type: 'numberInput',
  label: 'Left Gap',
  dataPath: selectorPaths.contentGaps.left
}

export const contentGapBack: NumberInput = {
  type: 'numberInput',
  label: 'Back Gap',
  dataPath: selectorPaths.contentGaps.back
}

/**
 *
 *
 */

export const columnX = 'x' as const
export const columnY = 'y' as const
export const columnZ = 'z' as const
export const column = 'column' as const

export const columns = [columnX, columnY, columnZ, column]

export type Column = Unpacked<typeof columns>

export type PropertyCell = {
  column: Column
  disabled?: boolean
} & InputType

type Area = {
  title: string
  visible?: 'editor' | ''
  cells: PropertyCell[]
}

type Table = Area[]

export const editorConfiguration = [
  // { title: 'Project' },
  { title: 'Content' },
  { title: 'Enclosure', alignSelf: 'stretch' }
]

export const summary: Table = [
  {
    title: 'General',
    cells: [
      { column, ...projectName },
      { column, ...projectDescription },
      { column, ...coverColor }
    ]
  },
  { title: 'Mounting', cells: [{ column, ...fastener }] },
  {
    title: 'PCB Dimensions',
    cells: [
      { column: columnX, ...printedcircuitboardWidth },
      { column: columnY, ...printedcircuitboardLength },
      { column: columnZ, ...printedcircuitboardThickness },
      { column: columnZ, ...printedcircuitboardComponentSideHeight },
      { column: columnZ, ...printedcircuitboardSolderSideHeight }
    ]
  },
  {
    title: 'PCB Placement',
    cells: [
      { column: columnX, ...contentGapFront },
      { column: columnY, ...contentGapRight },
      { column: columnZ, ...contentGapTop },
      { column: columnX, ...contentGapBack },
      { column: columnY, ...contentGapLeft },
      { column: columnZ, ...contentGapBottom }
    ]
  }
]

/**
 *
 *
 */

export interface Step {
  title: string
  path: string
}

export interface Field extends Step {
  type: 'field'
  description?: string
  inputs: InputType[]
  layers?: any
  perceptions?: any
}

export interface Stepper extends Step {
  type: 'stepper'
  steps: Field[]
}

export interface Summary extends Step {
  type: 'summary'
  table: Table
}

export const steps: (Field | Stepper)[] = [
  /* Step 1
  {
    type: 'field',
    title: 'Projects name & description',
    description:
      'You can use the name given here to identify the housing in both the Project Manager and the Housing Shop. Entering a brief description is mandatory.',
    path: 'name',
    inputs: [projectName, projectDescription],
    layers: {
      ...[
        ...contentEdgeLayers,
        ...accessoriesLayers,
        ...enclosureLayers,
        ...printedcircuitboardAssemblyLayers,
        fastenerLayer
      ].reduce((p, c) => ({ ...p, [c]: false }), {})
    },
    perceptions: {
      ...[
        typenames.cover,
        typenames.adapter,
        typenames.accessoryConnection,
        typenames.fastenerConnection,
        typenames.gap,
        typenames.componentSide,
        typenames.solderSide
      ].reduce((p, c) => ({ ...p, [c]: { visible: false } }), {})
    }
  },
  */
  // Step 2
  {
    type: 'stepper',
    title: 'PCB Geometry',
    path: 'geometry',
    steps: [
      {
        type: 'field',
        title: 'Plate Dimension',
        description:
          'Please enter the three dimensions for your rectangular circuit board here.',
        path: 'dimension',
        inputs: [
          printedcircuitboardWidth,
          printedcircuitboardLength,
          printedcircuitboardThickness
        ],
        layers: {
          ...[
            ...accessoriesLayers,
            ...enclosureLayers,
            ...printedcircuitboardAssemblyLayers,
            fastenerLayer
          ].reduce((p, c) => ({ ...p, [c]: false }), {})
        },
        perceptions: {
          ...[
            typenames.cover,
            typenames.adapter,
            typenames.accessoryConnection,
            typenames.fastenerConnection,
            typenames.gap,
            typenames.componentSide,
            typenames.solderSide
          ].reduce((p, c) => ({ ...p, [c]: { visible: false } }), {})
        }
      },
      {
        type: 'field',
        title: 'Assembly dimension',
        description:
          'Please enter the exact height of the highest component in each case once on the top (component side) and once on the bottom (solder side). You can take required air gaps into account in the next step.',
        path: 'assembly',
        inputs: [
          printedcircuitboardComponentSideHeight,
          printedcircuitboardSolderSideHeight
        ],
        perceptions: {
          ...[
            typenames.cover,
            typenames.adapter,
            typenames.accessoryConnection,
            typenames.gap,
            typenames.fastenerConnection
          ].reduce((p, c) => ({ ...p, [c]: { visible: false } }), {})
        }
      }
    ]
  },

  // Step 3
  {
    type: 'field',
    title: 'Assembly Mounting',
    description:
      'In the start-up phase, we only offer the tried-and-tested slide-in solution, which requires a 3mm wide free edge on the long sides of your assembly. The next solution will probably be screw mounting on spacers.',
    path: 'mounting',
    inputs: [fastener],
    perceptions: {
      ...[typenames.screws, typenames.nuts, typenames.gap].reduce(
        (p, c) => ({ ...p, [c]: { visible: false } }),
        {}
      ),
      [typenames.coverPlateEdge]: {
        selector: '/properties/side',
        ...[front, top, right, left, back].reduce(
          (p, c) => ({ ...p, [c]: { visible: false } }),
          {}
        )
      },
      [typenames.adapterPlateEdge]: {
        selector: '/properties/side',
        ...[front, top, right, left, back].reduce(
          (p, c) => ({ ...p, [c]: { visible: false } }),
          {}
        )
      }
    }
  },
  // Step 4
  {
    type: 'field',
    title: 'PCB Placement',
    description:
      'Air gaps ensure the desired safety distance between the module and the wall. This also defines the placement of the module within the housing. Because of the optional slide-in installation, the air gaps for the side walls are fixed.',
    path: 'placement',
    inputs: [
      contentGapFront,
      contentGapTop,
      contentGapRight,
      contentGapBottom,
      contentGapLeft,
      contentGapBack
    ],
    perceptions: {
      ...[typenames.screws, typenames.nuts].reduce(
        (p, c) => ({ ...p, [c]: { visible: false } }),
        {}
      ),
      [typenames.coverPlateEdge]: {
        selector: '/properties/side',
        ...[front, top, right, left, back].reduce(
          (p, c) => ({ ...p, [c]: { visible: false } }),
          {}
        )
      },
      [typenames.adapterPlateEdge]: {
        selector: '/properties/side',
        ...[front, top, right, left, back].reduce(
          (p, c) => ({ ...p, [c]: { visible: false } }),
          {}
        )
      }
    }
  },

  // Step 5
  {
    type: 'field',
    title: 'Cover Color',
    description:
      'We manufacture your enclosure from acrylic (thickness 3 mm). If you choose "transparent", the ceiling and floor slabs are always made in white.',
    path: 'color',
    inputs: [coverColor],
    perceptions: {}
  }
]

export const stepsConfiguration = steps.reduce(
  (p, c) => ({
    ...p,
    ...(c.type === 'field'
      ? {
          [c.path]: {
            route: c.path,
            layers: c.layers,
            perceptions: c.perceptions
          }
        }
      : (c as Stepper).steps.reduce(
          (sp, s) => ({
            ...sp,
            [`${c.path}/${s.path}`]: {
              route: `${c.path}/${s.path}`,
              layers: s.layers,
              perceptions: s.perceptions
            }
          }),
          {}
        ))
  }),
  {}
)

export const stepLinks = (basepath: string) =>
  steps.reduce(
    (p, c) => [
      ...p,
      ...(['field', 'summary'].includes(c.type)
        ? [`${basepath}/${c.path}`]
        : [
            ...(c as Stepper).steps.reduce(
              (sp, s) => [...sp, `${basepath}/${c.path}/${s.path}`],
              [] as string[]
            )
          ])
    ],
    [] as string[]
  ) as string[]
